import React, { useMemo, useState } from 'react';
import cn from '../../lib/cn';
import site from '../../data/site';
import { categories, pieces } from '../../data/collections';
import { Section, SectionHeading } from '../ui/Section';
import Reveal from '../ui/Reveal';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Lightbox from '../ui/Lightbox';

/** One catalogue tile. */
function PieceCard({ piece, index, onOpen }) {
  return (
    // Stagger across the row rather than the whole grid, so each row cascades
    // left-to-right instead of the last tiles waiting seconds for their turn.
    <Reveal
      variant="up"
      distance={26}
      duration={820}
      delay={(index % 4) * 90}
      className="group"
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
        aria-label={`View ${piece.name}`}
      >
        <div className="relative overflow-hidden rounded-sm bg-canvas-soft">
          <img
            src={piece.src}
            alt={piece.name}
            loading="lazy"
            decoding="async"
            width={1000}
            height={1000}
            className="aspect-square w-full object-cover transition-transform duration-[900ms] ease-silk group-hover:scale-[1.06]"
          />

          {/* Scrim + view affordance on hover. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-950/55 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          <span className="pointer-events-none absolute bottom-4 right-4 grid h-10 w-10 translate-y-3 place-items-center rounded-full bg-canvas text-ink-950 opacity-0 transition-all duration-400 ease-silk group-hover:translate-y-0 group-hover:opacity-100">
            <Icon name="arrowUpRight" size={17} />
          </span>
        </div>

        <div className="mt-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg leading-snug text-ink-950">{piece.name}</h3>
            <p className="mt-0.5 truncate text-[0.78rem] leading-relaxed text-ink-700/65">
              {piece.note}
            </p>
          </div>
          <span className="mt-1 shrink-0 text-[0.58rem] uppercase tracking-widest text-champagne-500">
            {categories.find((category) => category.id === piece.cat)?.label.split(' ')[0]}
          </span>
        </div>
      </button>
    </Reveal>
  );
}

/**
 * The catalogue. Filtering is client-side over a static list — there is no
 * cart or price, because every piece is quoted at the counter.
 */
export function Collections() {
  const [active, setActive] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const visible = useMemo(
    () => (active === 'all' ? pieces : pieces.filter((piece) => piece.cat === active)),
    [active]
  );

  return (
    <Section id="collections" tone="canvas">
      <div className="shell">
        <SectionHeading
          eyebrow="The Collections"
          title={['Pieces we would', 'put in your hands']}
          lede="A working selection from the showroom floor — bridal polki, antique gold, diamond and CZ, rings, bangles, chains and timepieces. Everything here has been photographed in store."
          action={
            <Button href={site.social.instagram} variant="outline" size="md">
              <Icon name="instagram" size={16} />
              See more on Instagram
            </Button>
          }
        />

        {/* Filter chips. */}
        <Reveal delay={120} className="mt-12">
          <div
            className="no-scrollbar -mx-[var(--shell-x)] flex gap-2 overflow-x-auto px-[var(--shell-x)] pb-1"
            role="tablist"
            aria-label="Filter collections"
          >
            {categories.map((category) => {
              const selected = active === category.id;
              return (
                <button
                  key={category.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActive(category.id)}
                  className={cn(
                    'shrink-0 rounded-full border px-5 py-2.5 font-sans text-[0.72rem] uppercase tracking-widest transition-all duration-300 ease-silk',
                    selected
                      ? 'border-transparent bg-ink-950 text-canvas'
                      : 'border-ink-900/15 text-ink-700/75 hover:border-ink-900/40 hover:text-ink-950'
                  )}
                >
                  {category.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div
          key={active}
          className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 sm:gap-x-6 lg:grid-cols-4 lg:gap-x-7"
        >
          {visible.map((piece, i) => (
            <PieceCard
              key={piece.slug}
              piece={piece}
              index={i}
              onOpen={() => setLightbox(i)}
            />
          ))}
        </div>

        <Reveal delay={80} variant="scale" duration={900} className="mt-14">
          <div className="rounded-sm border border-champagne-300/40 bg-canvas-soft px-6 py-6 sm:px-9 sm:py-7">
            <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-xl text-[0.92rem] leading-relaxed text-ink-700/85">
                Looking for something specific — a bridal set, a resize, or a valuation? Tell us
                what you have in mind and we will have it ready on the counter for your visit.
              </p>
              <Button href={site.contact.whatsappHref} variant="solid" size="md" className="shrink-0">
                <Icon name="whatsapp" size={16} />
                Ask about a piece
              </Button>
            </div>
          </div>
        </Reveal>
      </div>

      <Lightbox
        items={visible}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNavigate={setLightbox}
      />
    </Section>
  );
}

export default Collections;
